// Modal de detalhe / edição / baixa de pagamento de Contas a Pagar.
// Extraído de FinanceiroContasPagar.tsx durante refactor topológico.
//
// Contém também os sub-componentes AnexosLista, HistoricoLista e
// DetalheRow (todos usados exclusivamente por este modal).
import { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Modal, Form, Input, InputNumber, Select, Card, Row, Col, DatePicker, Tag, Button, Flex, Space, Tabs, Alert, Empty, Typography, App as AntApp,
  Popconfirm,
} from 'antd';
import {
  PlusOutlined, EditOutlined, CheckCircleOutlined, SyncOutlined, MinusCircleOutlined, UndoOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { API_URL } from '../../config';
import { fmtDataBr } from '../../utils/dataFmt';
import { ContaPagar } from './types';
import { fmt, getToken } from './utils';
import {
  ParteForm, emPartes, ehParteDaTela, faltaPagarCP, faltaDepois, origemDoPagamento, pagoDoCP,
  pagoEmParte, problemaNasPartes, situacaoCP, somaPartes, sugestaoProximaParte, valorDaBaixa, brl,
} from './pagamentosEmPartes';
import { opcoesFornecedor, filtrarFornecedor, FornecedorSlim } from '../../utils/fornecedores';
import { PROPS_DINHEIRO_BR } from '../../utils/dinheiroInput';
import { ModalNovoFornecedor } from './ModalNovoFornecedor';
import { PessoaDaEquipe, useFichasDaEquipe } from './PessoaDaEquipe';
import { pedeAPessoa, sugestaoDePessoa, campoDaPessoaNoSalvar } from '../../utils/pessoaDaDespesa';

const { Text } = Typography;

export function ModalDetalheCP({ conta, cutOverDate, modoInicial = 'detalhe', onFechar, onSalvou }: {
  conta: ContaPagar; cutOverDate?: string | null;
  modoInicial?: 'detalhe' | 'editar' | 'baixar';
  onFechar: () => void; onSalvou: () => void;
}) {
  const { message, modal } = AntApp.useApp();
  // Elegibilidade pra edição completa (Fase 2 cut-over):
  // - origem='manual' SEMPRE editável
  // - origem='conta_azul_api' E data_vencimento >= cutOverDate → editável
  // - antes do cut-over, fica somente leitura (CA é fonte da verdade do passado)
  // Usa data de vencimento (Marina gerencia por vencimento no dia-a-dia).
  const vencStr = (conta.data_vencimento || '').slice(0, 10);
  const posCutOver = !!cutOverDate && !!vencStr && vencStr >= cutOverDate;
  const editavel = conta.origem === 'manual' || posCutOver;
  const isCA = conta.origem === 'conta_azul_api';
  // modoInicial: vem do menu "Ações" — abre direto em editar / baixar.
  const [editando, setEditando] = useState(modoInicial === 'editar' && editavel);
  const [salvando, setSalvando] = useState(false);
  const [aba, setAba] = useState<'detalhe' | 'historico' | 'anexos'>('detalhe');
  const [historico, setHistorico] = useState<any[] | null>(null);
  const [anexos, setAnexos] = useState<any[] | null>(null);
  const [uploadando, setUploadando] = useState(false);
  const [baixando, setBaixando] = useState(false);
  // Reespelhar do CA: o sync está pausado (cut-over 15/07) mas o CA segue
  // operado — ajuste de baixa feito lá congela no espelho baixas_ca daqui,
  // e é o espelho que o fluxo/painel contam (caso Airbnb 812,49 × 1.973,19).
  const [reespelhando, setReespelhando] = useState(false);
  async function reespelharCA() {
    setReespelhando(true);
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/financeiro/conta-azul/reespelhar-parcela`,
        { tipo: 'cp', id: conta.id }, { headers: { Authorization: token } });
      const dep: any[] = r.data?.baixas_depois || [];
      const fmtB = (b: any) => `${fmt(Number(b.valor) || 0)} (${b.conta || '?'} em ${b.data ? fmtDataBr(b.data) : '?'})`;
      message.success(
        dep.length
          ? `Espelho atualizado do CA: ${dep.map(fmtB).join(' + ')}`
          : 'Espelho atualizado: a parcela está SEM baixas no CA.', 6);
      onSalvou();
      onFechar();
    } catch (e: any) {
      message.error(e?.response?.data?.error || e?.response?.data?.erro || 'Falha ao reespelhar do CA', 6);
    } finally { setReespelhando(false); }
  }
  // Pagamento em partes (07/10/2026): quem ainda não está pago registra os
  // pagamentos em linhas (conta, valor, data). O formulário antigo (`baixaForm`)
  // fica só para "Editar pagamento" de quem foi pago à moda antiga.
  const hojeIso = dayjs().format('YYYY-MM-DD');
  const faltaPagar = faltaPagarCP(conta);
  const pagoJa = pagoDoCP(conta);
  const pagoPorPartes = emPartes(conta);
  const novaParte = (valor: number | null, k: number): ParteForm => ({
    chave: `p${k}-${Date.now()}`,
    conta_financeira_id: (conta as any).conta_financeira_id || '',
    valor,
    data_pagamento: hojeIso,
    metodo_pagamento: conta.meio_pagamento || '',
  });
  const [partesForm, setPartesForm] = useState<ParteForm[] | null>(
    modoInicial === 'baixar' && editavel && conta.status !== 'pago' && conta.status !== 'cancelado'
      ? [novaParte(faltaPagar || null, 0)] : null);
  const [registrando, setRegistrando] = useState(false);
  const [desfazendo, setDesfazendo] = useState<string | null>(null);
  const [baixaForm, setBaixaForm] = useState<{ data_pagamento: string; conta: string; metodo: string; conta_id: string } | null>(
    modoInicial === 'baixar' && editavel && conta.status === 'pago' && !pagoPorPartes ? {
      data_pagamento: conta.data_pagamento_real || conta.data_pagamento || new Date().toISOString().slice(0, 10),
      conta: conta.conta_financeira_ca || '',
      conta_id: (conta as any).conta_financeira_id || '',
      metodo: conta.meio_pagamento || '',
    } : null
  );
  // F-CP-cc: extrai CC inicial do centros_custo se houver exatamente 1 item
  // (rateios Clockify ou splits manuais multi-CC ficam de fora dessa UI MVP —
  // são tratados pelo bloco visual de "Centros de Custo" no detalhe).
  const ccInicial = (Array.isArray(conta.centros_custo) && conta.centros_custo.length === 1
    && !(conta as any).rateado_clockify)
    ? (conta.centros_custo[0].centro || '')
    : '';
  // Lançamento com a despesa dividida entre vários centros (rateio do Conta
  // Azul ou split manual). O campo de cima não representa isso — ver o aviso.
  const temVariosCentros = Array.isArray(conta.centros_custo) && conta.centros_custo.length > 1;
  // Uma linha só, mas com categoria própria, TAMBÉM é rateio para efeito de
  // tela: se o editor não abrisse, a categoria ficaria invisível e o primeiro
  // salvar a apagaria (o caminho do Select único não sabe representá-la).
  const temCategoriaPorLinha = Array.isArray(conta.centros_custo)
    && conta.centros_custo.some((cc: any) => String(cc.categoria_dre || '').trim() !== '');
  const temRateioNaConta = temVariosCentros || temCategoriaPorLinha;
  // Rateio que veio pronto do Conta Azul fica em LEITURA (decisão do Lucas,
  // 27/08/2026: "quero poder editar rateio daqui pra frente, não do que foi
  // herdado do CA"). Lá a divisão é a verdade e o valor por centro nem vem na
  // API — nós dividimos em partes iguais; reescrever por aqui só criaria uma
  // terceira versão do mesmo número. O que nasce aqui, a gente edita aqui.
  //
  // "Nasceu aqui" se reconhece pela ASSINATURA das linhas: as nossas telas
  // gravam cc_contrato_numero/cc_contrato_titulo/categoria_dre; o espelho do
  // CA grava só {centro, valor} (medido em 31/08/2026: as 1.950 linhas dos
  // 357 rateios herdados não têm nenhuma dessas chaves). Sem esta distinção,
  // um rateio montado NA TELA sobre um lançamento de origem CA — a maioria:
  // 11.472 dos 11.632 — reabria travado como se fosse herdado.
  const rateioMontadoAqui = Array.isArray(conta.centros_custo)
    && conta.centros_custo.some((cc: any) =>
      'cc_contrato_numero' in cc || 'cc_contrato_titulo' in cc
      || String(cc.categoria_dre || '').trim() !== '');
  const rateioVeioDoCA = temVariosCentros && conta.origem === 'conta_azul_api' && !rateioMontadoAqui;
  // Crédito do cartão Ticket Log (07/10/2026): o rateio sai das notas conferidas
  // em Combustíveis — editar aqui seria desfeito (e o servidor recusa).
  const creditoCombustivel = !!(conta as any).credito_combustivel;
  const podeEditarRateio = !(conta as any).rateado_clockify && !rateioVeioDoCA && !creditoCombustivel;
  // Linhas do editor: centro + valor. Começa espelhando o que já existe.
  const [rateio, setRateio] = useState<Array<{ centro: string; valor: number; categoria_dre?: string }>>(
    () => (Array.isArray(conta.centros_custo) ? conta.centros_custo : [])
      .map((cc: any) => ({ centro: cc.centro || '', valor: Math.abs(Number(cc.valor) || 0),
                           categoria_dre: cc.categoria_dre || '' })));
  // Só entra no modo rateio quando a pessoa pede (ou quando já é um rateio
  // editável) — o caso comum de UM centro segue com o Select de sempre.
  const [modoRateio, setModoRateio] = useState(temRateioNaConta && podeEditarRateio);
  // "voltar para um centro só" desfaz uma divisão que existe no banco. O servidor
  // recusa encolher rateio sem confirmação (a trava de 25/08) — sem esta marca o
  // salvar seguinte morria em 409 e a pessoa ficava sem saída.
  const [rateioDesfeitoAqui, setRateioDesfeitoAqui] = useState(false);

  const [form, setForm] = useState({
    fornecedor_id: (conta as any).fornecedor_id || '',
    fornecedor: conta.fornecedor || '',
    descricao: conta.descricao || '',
    periodo: conta.periodo || '',
    valor: String(Math.abs(conta.valor || 0)),
    categoria_dre: conta.categoria_dre || '',
    grupo_dre: conta.grupo_dre || '',
    centro_custo: ccInicial,  // F-CP-cc: single CC (codigo_canonical)
    data_competencia: (conta.data_competencia || '').slice(0, 10),
    data_vencimento: (conta.data_vencimento || '').slice(0, 10),
    data_pagamento: (conta.data_pagamento || '').slice(0, 10),
    status: (conta.status || 'em_aberto') as 'em_aberto' | 'agendado' | 'pago' | 'cancelado',
    meio_pagamento: conta.meio_pagamento || '',
    conta_financeira: conta.conta_financeira_ca || '',
    conta_financeira_id: (conta as any).conta_financeira_id || '',  // FZ 5.2
    nota_fiscal_numero: conta.nota_fiscal_numero || '',
    observacoes: conta.observacoes || '',
  });

  // Pessoa da equipe (05/10/2026). O campo mostra o que VAI ser gravado: numa
  // remuneração que está sem pessoa, entra a sugestão pelo nome do fornecedor
  // (marcada como sugestão) e ela é salva junto com o resto. Só vai no PATCH
  // quando difere do que está gravado — quem só muda o vencimento não mexe nela.
  const pessoaOriginal: string = (conta as any).profissional_id || '';
  const [pessoa, setPessoa] = useState(pessoaOriginal);
  const [pessoaTocada, setPessoaTocada] = useState(false);
  const pessoaMexida = pessoa !== pessoaOriginal;
  // Linha legada (lancamentos_manuais) não tem pessoa: nem campo, nem carga da lista.
  const temCampoDePessoa = conta._fonte !== 'lancamentos_manuais';
  const { fichas, estado: estadoFichas, recarregar: recarregarFichas } = useFichasDaEquipe(
    temCampoDePessoa && editavel && (pedeAPessoa(form.categoria_dre) || !!pessoa));
  useEffect(() => {
    if (pessoaTocada || pessoaOriginal || estadoFichas !== 'ok') return;
    setPessoa(sugestaoDePessoa(form.categoria_dre, form.fornecedor, fichas));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pessoaTocada, pessoaOriginal, estadoFichas, fichas, form.categoria_dre, form.fornecedor]);

  // Metadados pra dropdowns (Fase 2A — fornecedor DB, categoria DRE, contas)
  const [fornecedores, setFornecedores] = useState<any[]>([]);
  const [categoriasDre, setCategoriasDre] = useState<any[]>([]);
  const [contasDisponiveis, setContasDisponiveis] = useState<string[]>([]);
  const [contasFin, setContasFin] = useState<any[]>([]);  // FZ 5.2 — cadastro oficial
  // Ativas + a desativada que já é o valor do campo (com o aviso no rótulo).
  const opcoesConta = (atual?: string | null) => contasFin
    .filter((c: any) => c.ativo !== false || c.id === atual)
    .map((c: any) => ({ label: (c.apelido || c.nome) + (c.ativo === false ? ' (desativada)' : ''), value: c.id }));
  const [centrosCusto, setCentrosCusto] = useState<any[]>([]);  // F-CP-cc
  const [novoFornAberto, setNovoFornAberto] = useState(false);
  // Fornecedor aberto no sub-modal de edição (CNPJ, razão social…).
  const [fornEditando, setFornEditando] = useState<FornecedorSlim | null>(null);
  const fornecedorSelecionado: FornecedorSlim | null =
    fornecedores.find((f: any) => f.id === form.fornecedor_id) || null;

  // Carrega metadados no MOUNT do modal (não só em modo edição) pra evitar
  // o Select mostrar UUID nu antes do fetch async completar.
  useEffect(() => {
    let cancelado = false;
    (async () => {
      try {
        const token = await getToken();
        const [rF, rC, rB, rCC, rCF] = await Promise.all([
          axios.get(`${API_URL}/fornecedores?ativo=true&limit=2000`, { headers: { Authorization: token } }),
          axios.get(`${API_URL}/categorias`, { headers: { Authorization: token } }).catch(() => ({ data: [] })),
          axios.get(`${API_URL}/contas-pagar?limit=1`, { headers: { Authorization: token } }).catch(() => ({ data: { contas_disponiveis: [] } })),
          // status=todos SEM filtro de tipo: os estruturais (Administrativo,
          // Comercial, Escritório...) precisam aparecer — o filtro tipo=projeto
          // escondia eles e a Marina não achava o CC administrativo (2026-07-21).
          axios.get(`${API_URL}/centros-custo?status=todos&simples=1`,
            { headers: { Authorization: token } }).catch(() => ({ data: [] })),
          // incluir_inativas: conta desativada que JÁ é a conta do lançamento
          // precisa de rótulo — sem ela o Select mostra o uuid cru (22/09/2026).
          axios.get(`${API_URL}/financeiro/contas?incluir_inativas=1`, { headers: { Authorization: token } }).catch(() => ({ data: [] })),
        ]);
        if (cancelado) return;
        const dF = rF.data.body ? JSON.parse(rF.data.body) : rF.data;
        setFornecedores(Array.isArray(dF) ? dF : []);
        const dC = rC.data.body ? JSON.parse(rC.data.body) : rC.data;
        setCategoriasDre(Array.isArray(dC) ? dC.filter((c: any) => c.linha_dre) : []);
        const dB = rB.data.body ? JSON.parse(rB.data.body) : rB.data;
        setContasDisponiveis(Array.isArray(dB?.contas_disponiveis) ? dB.contas_disponiveis : []);
        const dCC = rCC.data.body ? JSON.parse(rCC.data.body) : rCC.data;
        setCentrosCusto(Array.isArray(dCC) ? dCC : []);
        const dCF = rCF.data.body ? JSON.parse(rCF.data.body) : rCF.data;
        setContasFin(Array.isArray(dCF) ? dCF : []);
      } catch (e: any) {
        if (!cancelado) console.warn('Falha ao carregar metadados:', e.message);
      }
    })();
    return () => { cancelado = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function recarregarFornecedores(novoId?: string) {
    const token = await getToken();
    // 2000, não 500: são 951 fornecedores e a lista vem ordenada por nome —
    // com 500 a recarga cortava o alfabeto na metade e sumia com quem estava
    // depois do M até reabrir o modal. O carregamento inicial (l.120) já usa 2000.
    const r = await axios.get(`${API_URL}/fornecedores?ativo=true&limit=2000`,
      { headers: { Authorization: token } });
    const d = r.data.body ? JSON.parse(r.data.body) : r.data;
    setFornecedores(Array.isArray(d) ? d : []);
    if (novoId) {
      const f = (Array.isArray(d) ? d : []).find((x: any) => x.id === novoId);
      setForm(s => ({ ...s, fornecedor_id: novoId, fornecedor: f?.nome_fantasia || '' }));
    }
  }

  // Opções do Select de centro de custo — as mesmas no campo único e no
  // editor de rateio (indiretas depois dos projetos, fechados por último).
  const opcoesCC = [...centrosCusto]
    .sort((a: any, b: any) => ((a.fechado ? 2 : a.tipo === 'estrutural' ? 1 : 0)
      - (b.fechado ? 2 : b.tipo === 'estrutural' ? 1 : 0)))
    .map((cc: any) => ({
      label: `${cc.codigo_canonical}${cc.nome_completo && cc.nome_completo !== cc.codigo_canonical ? ` — ${cc.nome_completo}` : ''}`
        + (cc.tipo === 'estrutural' ? ' · indireta' : '') + (cc.fechado ? ' (fechado)' : ''),
      value: cc.codigo_canonical,
    }));

  // Categorias da DRE, as mesmas do campo do lançamento. O rótulo mostra o
  // grupo porque é ele que decide onde a fatia cai na DRE — e, no fechamento de
  // CC, se ela vira custo do contrato ou fica na empresa.
  // Sem as 'ignorada' (o servidor recusa com 400) e sem repetir a mesma linha:
  // categorias_dre tem uma linha por categoria do Conta Azul, várias apontando
  // para a mesma linha da DRE.
  const opcoesCategoria: Array<{ label: string; value: string }> = [];
  const _catsVistas = new Set<string>();
  categoriasDre.forEach((c: any) => {
    if (!c.linha_dre || !c.grupo_dre || c.status === 'ignorada') return;
    if (_catsVistas.has(c.linha_dre)) return;
    _catsVistas.add(c.linha_dre);
    opcoesCategoria.push({ label: `${c.linha_dre} (${c.grupo_dre})`, value: c.linha_dre });
  });

  // Conferência do rateio na tela: o que já foi distribuído × o valor do
  // lançamento. Quem digita precisa ver o buraco enquanto digita, não no erro
  // do servidor depois de salvar.
  const valorDoForm = Math.abs(parseFloat(form.valor) || 0);
  const somaRateio = Math.round(rateio.reduce((s, r) => s + (Math.abs(r.valor) || 0), 0) * 100) / 100;
  const difRateio = Math.round((valorDoForm - somaRateio) * 100) / 100;
  // Divide igual entre as linhas e joga o centavo do arredondamento na primeira,
  // pra soma fechar exata com o lançamento.
  function distribuirIgual() {
    if (rateio.length === 0) return;
    const base = Math.floor((valorDoForm / rateio.length) * 100) / 100;
    const sobra = Math.round((valorDoForm - base * rateio.length) * 100) / 100;
    setRateio(r => r.map((x, i) => ({ ...x, valor: i === 0 ? Math.round((base + sobra) * 100) / 100 : base })));
  }

  async function salvar() {
    if (!form.descricao.trim() || !form.valor) { message.warning('Descrição e valor são obrigatórios'); return; }
    const valorNum = -Math.abs(parseFloat(form.valor));
    if (isNaN(valorNum)) { message.warning('Valor inválido'); return; }
    // Rateio que não fecha grava despesa pela metade no fechamento de CC —
    // segurar aqui evita descobrir isso no relatório do mês seguinte.
    if (podeEditarRateio && modoRateio) {
      // Destino da linha é centro OU categoria — dá pra ratear só por
      // categoria, com o centro em branco (Lucas, 31/08/2026: "há casos, a
      // maioria, em que só quero ratear as categorias").
      const temDestino = (r: any) => r.centro || (r.categoria_dre || '').trim();
      const linhas = rateio.filter(r => temDestino(r) && Math.abs(r.valor) > 0);
      if (linhas.length === 0) {
        message.warning('Preencha pelo menos uma linha com centro de custo ou categoria, e um valor.', 6);
        return;
      }
      if (rateio.some(r => Math.abs(r.valor) > 0 && !temDestino(r))) {
        message.warning('Tem linha com valor mas sem destino — escolha um centro de custo ou uma categoria nela.', 6);
        return;
      }
      const chaves = linhas.map(r => `${r.centro || ''} ${(r.categoria_dre || '').trim()}`);
      if (new Set(chaves).size !== chaves.length) {
        message.warning('Tem duas linhas com o mesmo centro de custo e a mesma categoria — junte as linhas.', 6);
        return;
      }
      if (Math.abs(difRateio) > 0.02) {
        message.warning(`O rateio soma ${fmt(somaRateio)} e o lançamento é de ${fmt(valorDoForm)}`
          + ` — ${difRateio > 0 ? 'faltam' : 'sobram'} ${fmt(Math.abs(difRateio))}.`, 8);
        return;
      }
    }
    setSalvando(true);
    try {
      const token = await getToken();
      // FIN-7ajustes (2026-07-22): rotear pela TABELA real (_fonte), não pela
      // flag origem. Despesa "Nova despesa" e clone "(cópia)" têm origem='manual'
      // mas moram em lancamentos_pagar — mandavam pro /manuais (lancamentos_manuais)
      // e davam 404 "não encontrado" ao salvar. Só linhas legadas realmente em
      // lancamentos_manuais vão pro endpoint /manuais.
      if (conta._fonte === 'lancamentos_manuais') {
        // Endpoint legado pra origem manual (lancamentos_manuais)
        await axios.patch(`${API_URL}/manuais/${conta.id}`, {
          descricao: form.descricao.trim(), periodo: form.periodo, valor: valorNum,
          categoria_dre: form.categoria_dre || null, grupo_dre: form.grupo_dre || null,
          fornecedor_cliente: form.fornecedor || null,
          data_emissao: form.data_competencia || null,
          data_vencimento: form.data_vencimento || null,
          data_pagamento: form.data_pagamento || null,
          status: form.status, meio_pagamento: form.meio_pagamento || null,
          nota_fiscal_numero: form.nota_fiscal_numero || null,
          observacoes: form.observacoes || null,
          // O formulário sempre mostrou o campo de conta, mas este ramo legado
          // OMITIA do payload — "edita e não pega", sem erro (Marina, 2026-08-10).
          conta_financeira_id: form.conta_financeira_id || null,
        }, { headers: { Authorization: token } });
      } else {
        // Pós-cut-over: edita o lancamento_pagar diretamente (todos os campos CA-like).
        // F-CP-cc: monta centros_custo array a partir do CC selecionado. Se nenhum
        // CC escolhido, manda array vazio. Se tem rateio Clockify, NÃO sobrescreve
        // (preservaria via centros_custo_pre_clockify no backend).
        // 🚨 Rateio que veio do Conta Azul NÃO passa por aqui: lá a divisão é a
        // verdade, e mandar o que este modal sabe representar apagaria ela.
        // Medido em 25/08/2026: 357 lançamentos do CA com rateio, R$ 1,71
        // milhão, e um já tinha perdido a divisão assim. Mesma proteção que já
        // existia para o rateio do Clockify.
        // O grupo NÃO vai daqui: o servidor monta o par categoria+grupo a
        // partir do cadastro. Par trocado não dá erro — só produz uma DRE
        // plausível e errada.
        const enriquecer = (centro: string, valor: number, categoria?: string) => {
          const cc = centrosCusto.find((x: any) => x.codigo_canonical === centro);
          return {
            centro,
            valor: Math.abs(valor),
            cc_contrato_numero: cc?.codigo_canonical || null,
            cc_contrato_titulo: cc?.nome_completo || null,
            ...(categoria ? { categoria_dre: categoria } : {}),
          };
        };
        let centrosCustoPayload: any = undefined;
        if (podeEditarRateio) {
          if (modoRateio) {
            // Rateio montado na tela: manda as linhas preenchidas.
            centrosCustoPayload = rateio
              .filter(r => (r.centro || (r.categoria_dre || '').trim()) && Math.abs(r.valor) > 0)
              .map(r => enriquecer(r.centro, r.valor, (r.categoria_dre || '').trim() || undefined));
          } else if (form.centro_custo) {
            centrosCustoPayload = [enriquecer(form.centro_custo, valorNum)];
          } else {
            centrosCustoPayload = [];  // limpa CC
          }
        }
        await axios.patch(`${API_URL}/contas-pagar/${conta.id}`, {
          fornecedor_id: form.fornecedor_id || null,
          fornecedor_original: form.fornecedor || null,
          descricao: form.descricao.trim(),
          valor: valorNum,
          categoria_dre: form.categoria_dre || null,
          grupo_dre: form.grupo_dre || null,
          ...(centrosCustoPayload !== undefined ? { centros_custo: centrosCustoPayload } : {}),
          // A trava do servidor recusa encolher um rateio sem confirmação (ela
          // é o que impede o apagamento acidental). Quando a divisão foi montada
          // AQUI, a intenção é explícita — então a confirmação vai junto.
          ...(modoRateio || rateioDesfeitoAqui ? { confirmar_limpar_rateio: true } : {}),
          data_competencia: form.data_competencia || null,
          data_vencimento: form.data_vencimento || null,
          data_pagamento: form.data_pagamento || null,
          status: form.status,
          metodo_pagamento_ca: form.meio_pagamento || null,
          conta_financeira_ca: form.conta_financeira || null,
          conta_financeira_id: form.conta_financeira_id || null,
          observacoes: form.observacoes || null,
          // só fala da pessoa quando o campo difere do gravado (utils/pessoaDaDespesa)
          ...campoDaPessoaNoSalvar(pessoaOriginal, pessoa),
        }, { headers: { Authorization: token } });
      }
      message.success('Salvo.');
      onSalvou();
    } catch (e: any) {
      // Modal órfão: o lançamento foi excluído enquanto o modal estava aberto
      // (ex.: aba antiga). Fechar e recarregar em vez de deixar num beco.
      if (e.response?.status === 404) {
        message.warning('Este lançamento não existe mais (foi excluído). Recarregando a lista…', 5);
        onSalvou();
        onFechar();
        return;
      }
      message.error('Erro: ' + (e.response?.data?.error || e.response?.data?.erro || e.message));
    } finally { setSalvando(false); }
  }

  async function carregarHistorico() {
    if (conta._fonte === 'lancamentos_manuais') {
      // Linha legada em lancamentos_manuais usa outro modelo de eventos — pular.
      // CP manual em lancamentos_pagar (origem='manual') TEM histórico normal.
      setHistorico([]);
      return;
    }
    try {
      const token = await getToken();
      const r = await axios.get(`${API_URL}/contas-pagar/${conta.id}/historico`,
        { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      setHistorico(Array.isArray(d) ? d : []);
    } catch (e: any) {
      message.error('Erro ao carregar histórico: ' + (e.response?.data?.error || e.message));
      setHistorico([]);
    }
  }

  useEffect(() => {
    if (aba === 'historico' && historico === null) carregarHistorico();
    if (aba === 'anexos' && anexos === null) carregarAnexos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aba]);

  async function carregarAnexos() {
    try {
      const token = await getToken();
      const r = await axios.get(`${API_URL}/contas-pagar/${conta.id}/anexos`,
        { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      setAnexos(Array.isArray(d) ? d : []);
    } catch (e: any) {
      message.error('Erro ao carregar anexos: ' + (e.response?.data?.error || e.message));
      setAnexos([]);
    }
  }

  async function uploadAnexo(file: File) {
    setUploadando(true);
    try {
      const token = await getToken();
      // 1. Pega URL pré-assinada
      const r1 = await axios.post(`${API_URL}/contas-pagar/${conta.id}/anexos/upload-url`,
        { filename: file.name, content_type: file.type || 'application/octet-stream' },
        { headers: { Authorization: token } });
      const d1 = r1.data.body ? JSON.parse(r1.data.body) : r1.data;
      // 2. PUT direto no S3
      await axios.put(d1.url, file, { headers: { 'Content-Type': file.type || 'application/octet-stream' } });
      // 3. Confirma no backend
      await axios.post(`${API_URL}/contas-pagar/${conta.id}/anexos`,
        { s3_key: d1.s3_key, nome: file.name, tipo: file.type || null,
          tamanho_bytes: file.size, mime: file.type || null },
        { headers: { Authorization: token } });
      message.success(`Anexo "${file.name}" enviado.`);
      await carregarAnexos();
    } catch (e: any) {
      message.error('Erro ao enviar anexo: ' + (e.response?.data?.error || e.message));
    } finally { setUploadando(false); }
  }

  async function baixarAnexo(anexoId: string) {
    try {
      const token = await getToken();
      const r = await axios.get(`${API_URL}/contas-pagar/${conta.id}/anexos/${anexoId}/download-url`,
        { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      window.open(d.url, '_blank');
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.error || e.message));
    }
  }

  async function deletarAnexo(anexoId: string, nome: string) {
    modal.confirm({
      title: `Excluir anexo "${nome}"?`,
      content: 'Operação irreversível.',
      okText: 'Excluir', okType: 'danger', cancelText: 'Cancelar',
      onOk: async () => {
        try {
          const token = await getToken();
          await axios.delete(`${API_URL}/contas-pagar/${conta.id}/anexos/${anexoId}`,
            { headers: { Authorization: token } });
          message.success('Anexo excluído.');
          await carregarAnexos();
        } catch (e: any) {
          message.error('Erro: ' + (e.response?.data?.error || e.message));
        }
      },
    });
  }

  async function registrarPartes() {
    if (!partesForm) return;
    const problema = problemaNasPartes(partesForm, faltaPagar, hojeIso);
    if (problema) { message.warning(problema, 6); return; }
    setRegistrando(true);
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/contas-pagar/${conta.id}/baixar`, {
        partes: partesForm.map(p => ({
          conta_financeira_id: p.conta_financeira_id,
          valor: p.valor,
          data_pagamento: p.data_pagamento,
          metodo_pagamento: p.metodo_pagamento || null,
        })),
      }, { headers: { Authorization: token } });
      const d = r.data?.body ? JSON.parse(r.data.body) : r.data;
      message.success(d?.quitado
        ? 'Pagamento registrado — lançamento quitado.'
        : `Pagamento registrado. Ainda falta pagar ${brl(Number(d?.falta_pagar) || 0)}.`, 6);
      setPartesForm(null);
      onSalvou();
    } catch (e: any) {
      message.error(e.response?.data?.error || e.message, 8);
    } finally { setRegistrando(false); }
  }

  async function desfazerParte(parteId: string) {
    setDesfazendo(parteId);
    try {
      const token = await getToken();
      await axios.post(`${API_URL}/contas-pagar/${conta.id}/baixar`,
        { desfazer_pagamento: parteId }, { headers: { Authorization: token } });
      message.success('Pagamento desfeito.');
      onSalvou();
    } catch (e: any) {
      message.error(e.response?.data?.error || e.message, 8);
    } finally { setDesfazendo(null); }
  }

  async function aplicarBaixa() {
    if (!baixaForm?.data_pagamento) { message.warning('Data de pagamento obrigatória'); return; }
    setBaixando(true);
    try {
      const token = await getToken();
      await axios.post(`${API_URL}/contas-pagar/${conta.id}/baixar`, {
        data_pagamento: baixaForm.data_pagamento,
        conta_financeira_ca: baixaForm.conta || null,
        conta_financeira_id: baixaForm.conta_id || null,
        metodo_pagamento_ca: baixaForm.metodo || null,
      }, { headers: { Authorization: token } });
      message.success('Pagamento registrado.');
      setBaixaForm(null);
      onSalvou();
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.error || e.message));
    } finally { setBaixando(false); }
  }

  const st = situacaoCP(conta);
  const corOrigem = conta.origem === 'conta_azul_api' ? { color: 'green', label: '🔗 CA (API)' }
    : conta.origem === 'upload_xlsx' ? { color: 'blue', label: '📊 XLSX' }
    : { color: 'purple', label: '✍ Manual' };

  return (
    <Modal open onCancel={onFechar} width={1200} destroyOnClose
      centered
      styles={{ body: { maxHeight: 'calc(100vh - 220px)', overflowY: 'auto', overflowX: 'hidden', paddingTop: 8 } }}
      title={
        <div>
          <Text strong style={{ color: '#020D7F', fontSize: 16 }}>{conta.descricao || 'Lançamento'}</Text>
          <Space size={4} wrap style={{ marginTop: 6, display: 'flex' }}>
            <Tag color={corOrigem.color}>{corOrigem.label}</Tag>
            <Tag color={st.tagColor}>{st.label}</Tag>
            {conta.diferido && <Tag color="gold">diferido</Tag>}
            {!editavel && isCA && (
              <Tag color="default" title={cutOverDate ? `cut-over: ${new Date(cutOverDate).toLocaleDateString('pt-BR')}` : 'sem cut-over configurado'}>
                📅 Pré-cut-over (CA é fonte)
              </Tag>
            )}
            {!editavel && !isCA && <Tag>🔒 Só leitura</Tag>}
            {editavel && isCA && <Tag color="purple">✏️ Pós-cut-over (editável)</Tag>}
          </Space>
        </div>
      }
      footer={
        editando ? (
          <Space>
            <Button onClick={() => setEditando(false)} disabled={salvando}>Cancelar</Button>
            <Button type="primary" loading={salvando} onClick={salvar}>Salvar</Button>
          </Space>
        ) : (
          <Space>
            {isCA && (
              <Button icon={<SyncOutlined />} loading={reespelhando} onClick={reespelharCA}
                title="Busca no Conta Azul as baixas atuais desta parcela e atualiza o espelho aqui — use quando a baixa foi ajustada lá e o valor/data daqui ficou pra trás">
                Reespelhar do CA
              </Button>
            )}
            {editavel && conta.status !== 'pago' && conta.status !== 'cancelado' && (
              <Button icon={<CheckCircleOutlined />}
                onClick={() => setPartesForm([novaParte(faltaPagar || null, 0)])}>
                {pagoJa > 0 ? 'Registrar outro pagamento' : 'Baixar pagamento'}
              </Button>
            )}
            <Button type="primary" ghost icon={<EditOutlined />} disabled={!editavel}
              onClick={() => setEditando(true)}
              title={
                editavel ? 'Editar'
                : isCA ? 'Lançamento antes do cut-over — edite no Conta Azul'
                : 'Sincronizado — edite na origem'
              }>
              Editar
            </Button>
          </Space>
        )
      }>
      {!editando && (
        <Tabs activeKey={aba} onChange={(k) => setAba(k as any)}
          items={[
            { key: 'detalhe', label: 'Detalhe' },
            { key: 'anexos', label: `📎 Anexos${anexos ? ` (${anexos.length})` : ''}` },
            { key: 'historico', label: `Histórico${historico ? ` (${historico.length})` : ''}` },
          ]}
          size="small" style={{ marginBottom: 8 }} />
      )}
      {creditoCombustivel && (
        <Alert type="info" showIcon style={{ marginBottom: 8 }}
          message="Crédito do cartão Ticket Log (pré-pago)"
          description={<>O saldo deste crédito fica livre: cada nota de abastecimento conferida em
            {' '}<b>Financeiro › Pagamentos › Combustíveis</b> é descontada dele e vira despesa no mês do abastecimento,
            no centro de custo da nota. O rateio abaixo é atualizado sozinho a partir das notas — para registrar um
            consumo, use <b>Lançar nota</b> na aba Combustíveis.</>} />
      )}
      {!editando && aba === 'historico' ? (
        <HistoricoLista historico={historico} />
      ) : !editando && aba === 'anexos' ? (
        <AnexosLista anexos={anexos} uploadando={uploadando}
          onUpload={uploadAnexo} onBaixar={baixarAnexo} onDeletar={deletarAnexo}
          podeEditar={editavel} />
      ) : editando ? (
        <Form layout="vertical" size="small">
          <Card size="small" title="Informações do lançamento" style={{ marginBottom: 12 }}>
            <Row gutter={10}>
              <Col span={12}>
                <Form.Item label="Fornecedor">
                  <Flex gap={4}>
                    <Select
                      value={form.fornecedor_id || undefined}
                      onChange={(v) => {
                        const f = fornecedores.find((x: any) => x.id === v);
                        setForm({ ...form, fornecedor_id: v || '', fornecedor: f?.nome_fantasia || '' });
                      }}
                      placeholder={fornecedores.length === 0 ? 'Carregando fornecedores…' : 'Buscar fornecedor'}
                      allowClear showSearch filterOption={filtrarFornecedor}
                      loading={fornecedores.length === 0}
                      options={(() => {
                        const opts = opcoesFornecedor(fornecedores, { avisarSemCnpj: true });
                        // Fallback: se o fornecedor selecionado ainda não está na lista
                        // (lista carregando ou fornecedor inativo), adiciona com o nome
                        // textual pra evitar exibir UUID nu.
                        if (form.fornecedor_id && !fornecedores.find((f: any) => f.id === form.fornecedor_id)) {
                          opts.unshift({
                            label: form.fornecedor || '(carregando…)',
                            value: form.fornecedor_id,
                            _busca: (form.fornecedor || '').toLowerCase(),
                            _digitos: '',
                          });
                        }
                        return opts;
                      })()}
                      style={{ flex: 1, minWidth: 0 }} />
                    <Button size="small" icon={<PlusOutlined />} style={{ flexShrink: 0 }} onClick={() => setNovoFornAberto(true)}
                      title="Cadastrar novo fornecedor" />
                    {/* Editar o fornecedor escolhido: é por aqui que os 939 sem
                        CNPJ deixam de ser indistinguíveis na lista. */}
                    <Button size="small" icon={<EditOutlined />} style={{ flexShrink: 0 }}
                      disabled={!fornecedorSelecionado}
                      onClick={() => setFornEditando(fornecedorSelecionado)}
                      title={fornecedorSelecionado
                        ? `Editar ${fornecedorSelecionado.nome_fantasia} (CNPJ, razão social…)`
                        : 'Escolha um fornecedor para editar'} />
                  </Flex>
                  {/* FIN-UI (2026-07-14): texto livre removido — fornecedor só do
                      cadastro. Nome legado (linhas antigas sem vínculo) fica
                      visível como leitura pra não perder a informação. */}
                  {!form.fornecedor_id && form.fornecedor && (
                    <div style={{ marginTop: 6, fontSize: 11, color: '#888' }}>
                      Nome legado (texto): <b>{form.fornecedor}</b> — selecione ou cadastre o fornecedor pra vincular.
                    </div>
                  )}
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label="Data de competência" required>
                  <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY"
                    value={form.data_competencia ? dayjs(form.data_competencia) : null}
                    onChange={(d) => setForm({ ...form, data_competencia: d ? d.format('YYYY-MM-DD') : '' })} />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label="Valor (R$)" required>
                  <Input type="number" step="0.01" min={0} value={form.valor}
                    onChange={e => setForm({ ...form, valor: e.target.value })} placeholder="0,00" />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item label="Descrição" required>
                  <Input value={form.descricao} onChange={e => setForm({ ...form, descricao: e.target.value })} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Categoria">
                  <Select value={form.categoria_dre || undefined}
                    onChange={(v) => {
                      const c = categoriasDre.find((c: any) => c.linha_dre === v);
                      setForm({ ...form, categoria_dre: v || '', grupo_dre: c?.grupo_dre || form.grupo_dre });
                    }}
                    placeholder="Selecione…" allowClear showSearch optionFilterProp="label"
                    options={categoriasDre.map((c: any) => ({
                      label: `${c.linha_dre} (${c.grupo_dre})`,
                      value: c.linha_dre,
                    }))} />
                </Form.Item>
              </Col>
              {temCampoDePessoa && (pedeAPessoa(form.categoria_dre) || !!pessoa) && (
                <Col span={12}>
                  <PessoaDaEquipe linha={form.categoria_dre} escolha={pessoa}
                    sugerida={!pessoaTocada && pessoaMexida && !!pessoa}
                    fichas={fichas} estado={estadoFichas} recarregar={recarregarFichas}
                    rotuloAtual={(conta as any).profissional_nome || null}
                    onEscolher={(v) => { setPessoa(v); setPessoaTocada(true); }} />
                </Col>
              )}
              <Col span={modoRateio ? 24 : 12}>
                {/* F-CP-cc: Centro de Custo. Um centro pelo Select; vários pelo
                    editor de rateio (Lucas, 27/08/2026 — pedido do financeiro).
                    Rateio herdado do Conta Azul continua em leitura. */}
                <Form.Item label={modoRateio ? 'Rateio (centro de custo e/ou categoria)' : 'Centro de Custo'}
                  extra={(conta as any).rateado_clockify
                    ? '⏱️ Rateado via Clockify — desfaça o rateio pra editar aqui.'
                    : rateioVeioDoCA
                      ? `Dividido entre ${(conta.centros_custo as any[]).length} centros (veja a lista abaixo).`
                        + ' A divisão vem do Conta Azul — para mudar, edite lá e ressincronize.'
                        + ' Salvar por aqui não encosta nela.'
                      : modoRateio
                        ? 'A soma das linhas tem que fechar com o valor do lançamento.'
                          + ' Cada linha precisa de um centro de custo OU de uma categoria —'
                          + ' para ratear só por categoria, deixe o centro em branco.'
                          + ' Categoria em branco usa a do lançamento; centro repetido vale,'
                          + ' desde que a categoria mude.'
                        : 'Projeto/contrato ou centro de indiretas (Administrativo, Comercial…).'}>
                  {modoRateio ? (
                    <Space direction="vertical" size={6} style={{ width: '100%' }}>
                      <Flex gap={8} style={{ fontSize: 11, color: '#888' }}>
                        <div style={{ flex: 1, minWidth: 0 }}>Centro de custo (opcional)</div>
                        <div style={{ flex: 1, minWidth: 0 }}>Categoria da DRE (opcional)</div>
                        <div style={{ width: 150 }}>Valor</div>
                        <div style={{ width: 32 }} />
                      </Flex>
                      {rateio.map((linha, i) => (
                        <Flex key={i} gap={8} align="center">
                          <Select value={linha.centro || undefined}
                            onChange={(v) => setRateio(r => r.map((x, k) => k === i ? { ...x, centro: v || '' } : x))}
                            placeholder={centrosCusto.length === 0 ? 'Carregando…'
                              : (linha.categoria_dre || '').trim() ? 'sem centro (só categoria)' : 'Centro de custo'}
                            allowClear showSearch optionFilterProp="label" style={{ flex: 1, minWidth: 0 }}
                            loading={centrosCusto.length === 0} options={opcoesCC} />
                          {/* Categoria por linha: em branco = usa a categoria do
                              lançamento (o campo "Categoria" acima). Preenchida,
                              esta fatia vai pra outra linha da DRE. */}
                          <Select value={linha.categoria_dre || undefined}
                            onChange={(v) => setRateio(r => r.map((x, k) => k === i ? { ...x, categoria_dre: v || '' } : x))}
                            placeholder={form.categoria_dre
                              ? `mesma do lançamento (${form.categoria_dre})`
                              : 'mesma do lançamento'}
                            allowClear showSearch optionFilterProp="label"
                            style={{ flex: 1, minWidth: 0 }} options={opcoesCategoria} />
                          {/* 🚨 decimalSeparator + parser NÃO são enfeite: sem eles o
                              parser padrão do antd APAGA a vírgula ("10,50" virava
                              1050; "0,05" virava 5) e o rateio nunca fechava com o
                              lançamento — a queixa "não dá pra pôr centavos"
                              (Lucas, 02/09/2026). Régua: utils/dinheiroInput.ts. */}
                          <InputNumber value={linha.valor} style={{ width: 150 }}
                            onChange={(v) => setRateio(r => r.map((x, k) => k === i ? { ...x, valor: Number(v) || 0 } : x))}
                            {...PROPS_DINHEIRO_BR}
                            precision={2} min={0} prefix="R$" placeholder="0,00" />
                          <Button size="small" danger type="text" title="Remover esta linha"
                            onClick={() => setRateio(r => r.filter((_, k) => k !== i))}>✕</Button>
                        </Flex>
                      ))}
                      <Flex gap={12} align="center" wrap="wrap">
                        <Button size="small" onClick={() => setRateio(r => [...r, { centro: '', valor: 0, categoria_dre: '' }])}>
                          + centro
                        </Button>
                        <Button size="small" type="link" onClick={() => distribuirIgual()}
                          disabled={rateio.length === 0}>
                          dividir igual
                        </Button>
                        <Text style={{ fontSize: 12 }}>
                          Somado: <strong>{fmt(somaRateio)}</strong> de {fmt(Math.abs(valorDoForm))}
                          {Math.abs(difRateio) > 0.005 && (
                            <Text type="danger" style={{ fontSize: 12 }}>
                              {' '}· {difRateio > 0 ? 'faltam' : 'sobram'} {fmt(Math.abs(difRateio))}
                            </Text>
                          )}
                          {Math.abs(difRateio) <= 0.005 && rateio.length > 0 && (
                            <Text type="success" style={{ fontSize: 12 }}> · fecha ✓</Text>
                          )}
                        </Text>
                        <Button size="small" type="link" onClick={() => {
                          const desfazer = () => {
                            setModoRateio(false);
                            setRateio([]);
                            setRateioDesfeitoAqui(true);
                          };
                          if (rateio.filter(r => r.centro || (r.categoria_dre || '').trim()).length > 1) {
                            modal.confirm({
                              title: 'Desfazer a divisão?',
                              content: `Este lançamento fica com um centro de custo só.`
                                + ` As ${rateio.filter(r => r.centro || (r.categoria_dre || '').trim()).length} linhas e as categorias delas são descartadas.`,
                              okText: 'Desfazer', cancelText: 'Cancelar', okButtonProps: { danger: true },
                              onOk: desfazer,
                            });
                          } else { desfazer(); }
                        }}>
                          voltar para um centro só
                        </Button>
                      </Flex>
                    </Space>
                  ) : (
                    <>
                      <Select value={form.centro_custo || undefined}
                        onChange={(v) => setForm({ ...form, centro_custo: v || '' })}
                        placeholder={creditoCombustivel
                          ? 'Crédito do Ticket Log — o uso sai das notas em Combustíveis'
                          : rateioVeioDoCA
                          ? `${(conta.centros_custo as any[]).length} centros — rateio do Conta Azul`
                          : centrosCusto.length === 0 ? 'Carregando…' : 'Selecione o centro de custo'}
                        allowClear showSearch optionFilterProp="label"
                        disabled={(conta as any).rateado_clockify || rateioVeioDoCA || creditoCombustivel}
                        loading={centrosCusto.length === 0}
                        options={opcoesCC} />
                      {podeEditarRateio && (
                        <Button size="small" type="link" style={{ padding: 0, marginTop: 2 }}
                          onClick={() => {
                            setModoRateio(true);
                            setRateio(form.centro_custo
                              ? [{ centro: form.centro_custo, valor: Math.abs(valorDoForm), categoria_dre: '' }]
                              : [{ centro: '', valor: 0, categoria_dre: '' }]);
                          }}>
                          dividir entre vários centros
                        </Button>
                      )}
                    </>
                  )}
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item label="NF nº">
                  <Input value={form.nota_fiscal_numero} onChange={e => setForm({ ...form, nota_fiscal_numero: e.target.value })} />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          <Card size="small" title="Condição de pagamento" style={{ marginBottom: 12 }}>
            <Row gutter={10}>
              <Col span={8}>
                <Form.Item label="Vencimento">
                  <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY"
                    value={form.data_vencimento ? dayjs(form.data_vencimento) : null}
                    onChange={(d) => setForm({ ...form, data_vencimento: d ? d.format('YYYY-MM-DD') : '' })} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="Status"
                  extra={pagoPorPartes ? 'Pago em partes: a situação vem dos pagamentos.' : undefined}>
                  <Select value={form.status} onChange={(v) => setForm({ ...form, status: v as any })}
                    disabled={pagoPorPartes}
                    options={[
                      { label: 'Em aberto', value: 'em_aberto' },
                      { label: 'Agendado', value: 'agendado' },
                      { label: 'Pago', value: 'pago' },
                      { label: 'Cancelado', value: 'cancelado' },
                    ]} />
                </Form.Item>
              </Col>
              {form.status === 'pago' && (
                <Col span={8}>
                  <Form.Item label="Data do pagamento">
                    <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" disabled={pagoPorPartes}
                      value={form.data_pagamento ? dayjs(form.data_pagamento) : null}
                      onChange={(d) => setForm({ ...form, data_pagamento: d ? d.format('YYYY-MM-DD') : '' })} />
                  </Form.Item>
                </Col>
              )}
              <Col span={12}>
                <Form.Item label="Forma de pagamento">
                  <Select value={form.meio_pagamento || undefined}
                    onChange={(v) => setForm({ ...form, meio_pagamento: v || '' })}
                    allowClear placeholder="—"
                    options={[
                      { label: 'PIX', value: 'PIX' }, { label: 'Boleto', value: 'Boleto' },
                      { label: 'TED', value: 'TED' }, { label: 'Cartão', value: 'Cartão' },
                      { label: 'Dinheiro', value: 'Dinheiro' }, { label: 'Cheque', value: 'Cheque' },
                    ]} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Conta de pagamento">
                  {/* FZ 5.2: cadastro oficial (manda id + nome); textos do
                      legado CA como fallback se o cadastro não carregar. */}
                  <Select
                    value={form.conta_financeira_id || form.conta_financeira || undefined}
                    onChange={(v) => {
                      const cf = contasFin.find((c: any) => c.id === v);
                      setForm({
                        ...form,
                        conta_financeira_id: cf ? cf.id : '',
                        conta_financeira: cf ? (cf.apelido || cf.nome) : (v || ''),
                      });
                    }}
                    allowClear showSearch optionFilterProp="label" placeholder="—"
                    options={contasFin.length
                      ? opcoesConta(form.conta_financeira_id)
                      : contasDisponiveis.map(c => ({ label: c, value: c }))} />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          <Card size="small" title="Observações">
            <Form.Item style={{ marginBottom: 0 }}>
              <Input.TextArea value={form.observacoes} rows={3}
                onChange={e => setForm({ ...form, observacoes: e.target.value })}
                placeholder="Anotações livres sobre o lançamento" />
            </Form.Item>
          </Card>

          {/* Sub-modal: cadastro rápido de fornecedor */}
          {novoFornAberto && (
            <ModalNovoFornecedor
              onFechar={() => setNovoFornAberto(false)}
              onCriou={(id) => { setNovoFornAberto(false); recarregarFornecedores(id); }} />
          )}
          {/* Sub-modal: editar o fornecedor já escolhido */}
          {fornEditando && (
            <ModalNovoFornecedor
              fornecedor={fornEditando}
              onFechar={() => setFornEditando(null)}
              onCriou={(id) => { setFornEditando(null); recarregarFornecedores(id || form.fornecedor_id); }} />
          )}
        </Form>
      ) : (
        <div>
          {/* ─── HERO HEADER: valor + status + datas-chave em 3 colunas ─── */}
          <Card size="small" style={{ marginBottom: 14,
            background: 'linear-gradient(135deg, #FAFBFF 0%, #F0F4FF 100%)',
            border: '1px solid #D8E0FF' }} bodyStyle={{ padding: 18 }}>
            <Row gutter={[24, 12]} align="middle">
              <Col xs={24} md={10}>
                <Text type="secondary" style={{ fontSize: 10, letterSpacing: 0.5, textTransform: 'uppercase' }}>Total</Text>
                <div style={{ fontSize: 36, fontWeight: 800, color: '#B91626', lineHeight: 1.1, marginTop: 4 }}>
                  {fmt(conta.valor)}
                </div>
                {(conta.categoria_dre || conta.grupo_dre) && (
                  <Text style={{ fontSize: 11, color: '#666' }}>
                    {conta.categoria_dre || '—'}
                    {conta.grupo_dre && <span style={{ color: '#888' }}> · {conta.grupo_dre}</span>}
                  </Text>
                )}
              </Col>
              <Col xs={12} md={7}>
                <Text type="secondary" style={{ fontSize: 10, letterSpacing: 0.5, textTransform: 'uppercase' }}>Vencimento</Text>
                <div style={{ fontSize: 18, fontWeight: 600, color: '#020D7F', lineHeight: 1.2, marginTop: 4 }}>
                  {conta.data_vencimento ? fmtDataBr(conta.data_vencimento) : '—'}
                </div>
                <Text style={{ fontSize: 11, color: '#888' }}>
                  Competência: {conta.data_competencia ? fmtDataBr(conta.data_competencia) : '—'}
                </Text>
              </Col>
              <Col xs={12} md={7}>
                <Text type="secondary" style={{ fontSize: 10, letterSpacing: 0.5, textTransform: 'uppercase' }}>Pagamento</Text>
                <div style={{ fontSize: 18, fontWeight: 600,
                  color: conta.data_pagamento ? '#0a7f4a' : '#E07A1F',
                  lineHeight: 1.2, marginTop: 4 }}>
                  {conta.data_pagamento ? fmtDataBr(conta.data_pagamento)
                    : pagoEmParte(conta) ? `falta ${brl(faltaPagar)}` : '— (em aberto)'}
                </div>
                <Text style={{ fontSize: 11, color: '#888' }}>
                  {pagoEmParte(conta) ? `Pago em parte: ${brl(pagoJa)} até agora`
                    : pagoPorPartes && conta.data_pagamento ? 'Data do último pagamento'
                    : conta.data_pagamento_real ? 'Data REAL (baixa CA)' : conta.data_pagamento ? 'Conciliação CA' : 'Aguardando baixa'}
                </Text>
              </Col>
            </Row>
          </Card>

          {/* ─── GRID 2-COL: seções compactas lado-a-lado ─── */}
          <Row gutter={12} style={{ marginBottom: 4 }}>
            <Col xs={24} md={12}>
              <Card size="small" title="📅 Datas e período" style={{ marginBottom: 12 }} bodyStyle={{ padding: 12 }}>
                <DetalheRow label="Competência" valor={conta.data_competencia ? fmtDataBr(conta.data_competencia) : '—'}
                  hint="Mês contábil — DRE usa essa data" />
                <DetalheRow label="Vencimento" valor={conta.data_vencimento ? fmtDataBr(conta.data_vencimento) : '—'} hint="Data limite" />
                <DetalheRow label="Pagamento (caixa)" valor={conta.data_pagamento ? fmtDataBr(conta.data_pagamento) : '— (em aberto)'}
                  hint={conta.data_pagamento_real ? 'Data REAL (baixa da parcela CA)' : 'Quando saiu do banco'} />
                <DetalheRow label="Período" valor={conta.periodo} />
              </Card>
            </Col>
            <Col xs={24} md={12}>
              <Card size="small" title="🏷️ Classificação" style={{ marginBottom: 12 }} bodyStyle={{ padding: 12 }}>
                <DetalheRow label="Categoria" valor={conta.categoria_dre || '—'} />
                <DetalheRow label="Grupo DRE" valor={conta.grupo_dre || '—'} />
                {Array.isArray(conta.centros_custo) && conta.centros_custo.length === 1 && !conta.rateado_clockify && (
                  <DetalheRow label="Centro de custo"
                    valor={conta.centros_custo[0].cc_contrato_titulo
                      ? `${conta.centros_custo[0].cc_contrato_numero || ''} — ${conta.centros_custo[0].cc_contrato_titulo}`.trim()
                      : (conta.centros_custo[0].centro || '—')} />
                )}
                {Array.isArray(conta.centros_custo) && conta.centros_custo.length > 1 && (
                  <DetalheRow label="Centro de custo"
                    valor={(() => {
                      // Um rateio só por categoria não tem centro nenhum —
                      // dizer "2 centros" aqui afirmaria o contrário do que a
                      // pessoa acabou de fazer.
                      const n = conta.centros_custo.length;
                      const comCentro = conta.centros_custo.filter((cc: any) => cc.centro).length;
                      if (conta.rateado_clockify) return `${n} centros (rateio Clockify)`;
                      if (comCentro === 0) return `${n} linhas (rateio só por categoria)`;
                      if (comCentro < n) return `${n} linhas (centros e categorias)`;
                      return `${n} centros (split)`;
                    })()} />
                )}
              </Card>
            </Col>
            <Col xs={24} md={12}>
              <Card size="small" title="🏢 Fornecedor / Documento" style={{ marginBottom: 12 }} bodyStyle={{ padding: 12 }}>
                <DetalheRow label="Fornecedor" valor={conta.fornecedor || '—'} />
                {/* Linha legada não tem pessoa (não mostra). O convite "edite para
                    escolher" só aparece onde dá para editar. */}
                {temCampoDePessoa && ((conta as any).profissional_nome || pedeAPessoa(conta.categoria_dre)) && (
                  <DetalheRow label="Pessoa da equipe"
                    valor={(conta as any).profissional_nome
                      || (editavel ? '— sem pessoa (edite para escolher)' : '— sem pessoa')} />
                )}
                <DetalheRow label="Emissor (loja)" valor={conta.emissor || '—'} />
                <DetalheRow label="NF nº" valor={conta.nota_fiscal_numero || '—'} />
                <DetalheRow label="Meio de pagto" valor={conta.meio_pagamento || '—'} />
              </Card>
            </Col>
            <Col xs={24} md={12}>
              <Card size="small" title="🏦 Conta e conciliação" style={{ marginBottom: 12 }} bodyStyle={{ padding: 12 }}>
                <DetalheRow label="Conta financeira"
                  valor={(conta as any).conta_financeira_apelido || conta.conta_financeira_ca || '—'}
                  hint="Conta bancária do pagamento (fluxo por banco)" />
                <DetalheRow label="Conciliado" valor={
                  conta.conciliado_ca === true ? '✓ sim — bate com extrato'
                  : conta.conciliado_ca === false ? '✗ não conciliado' : '—'
                } />
              </Card>
            </Col>
          </Row>

          {Array.isArray(conta.centros_custo) && conta.centros_custo.length > 0 && (
            <Card size="small" style={{ marginBottom: 12 }} bodyStyle={{ padding: 12 }}
              title={conta.rateado_clockify
                ? `⏱️ Rateio Clockify aplicado (${conta.centros_custo.length} split${conta.centros_custo.length > 1 ? 's' : ''})`
                : conta.centros_custo.some((cc: any) => cc.centro)
                  ? `🎯 Centros de Custo (${conta.centros_custo.length})`
                  : `🎯 Rateio por categoria (${conta.centros_custo.length} linhas)`}>
              {conta.rateado_clockify && (
                <div style={{ fontSize: 11, color: '#666', marginBottom: 8, padding: '6px 8px', background: '#FFF7E6', borderLeft: '3px solid #FA8C16', borderRadius: 4 }}>
                  Lançamento rateado via horas Clockify. Cada item tem sua própria categoria DRE.
                  Pode ser revertido em <strong>Rateio Mensal (Clockify) → Desaplicar</strong>.
                </div>
              )}
              <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ color: '#888', background: '#FAFBFF' }}>
                    <th style={{ padding: '4px 6px', textAlign: 'left' }}>Tipo</th>
                    <th style={{ padding: '4px 6px', textAlign: 'left' }}>Categoria DRE</th>
                    <th style={{ padding: '4px 6px', textAlign: 'left' }}>CC / Projeto Clockify</th>
                    <th style={{ padding: '4px 6px', textAlign: 'right' }}>Horas</th>
                    <th style={{ padding: '4px 6px', textAlign: 'right' }}>Valor</th>
                  </tr>
                </thead>
                <tbody>
                  {conta.centros_custo.map((cc, i) => {
                    const tipo = cc.centro
                      ? 'projeto'
                      : (cc.categoria_dre ? 'nao_projeto' : 'plain');
                    const horas = typeof cc.horas_seg === 'number'
                      ? `${Math.floor(cc.horas_seg / 3600)}h${String(Math.floor((cc.horas_seg % 3600) / 60)).padStart(2, '0')}`
                      : '—';
                    return (
                      <tr key={i} style={{ borderTop: '0.5px solid #f0f0f0' }}>
                        <td style={{ padding: '4px 6px' }}>
                          {tipo === 'projeto' && <Tag color="blue" style={{ margin: 0, fontSize: 10 }}>CC Projeto</Tag>}
                          {tipo === 'nao_projeto' && <Tag color="orange" style={{ margin: 0, fontSize: 10 }}>Não-projeto</Tag>}
                          {tipo === 'plain' && <Tag style={{ margin: 0, fontSize: 10 }}>CC</Tag>}
                        </td>
                        <td style={{ padding: '4px 6px', color: '#444' }}>
                          {cc.categoria_dre || <span style={{ color: '#999' }}>—</span>}
                        </td>
                        <td style={{ padding: '4px 6px', fontSize: 10 }}>
                          {cc.cc_contrato_numero ? (
                            <>
                              <div style={{ fontWeight: 500 }}>{cc.cc_contrato_numero}</div>
                              {cc.cc_contrato_titulo && (
                                <div style={{ color: '#888' }}>{cc.cc_contrato_titulo}</div>
                              )}
                            </>
                          ) : cc.clockify_project_nome ? (
                            <span style={{ color: '#FA8C16', fontWeight: 500 }}>{cc.clockify_project_nome}</span>
                          ) : cc.centro ? (
                            /* O corte em 8 caracteres nasceu do rateio Clockify, que
                               gravava UUID aqui. O rateio do Conta Azul grava o rótulo
                               legível ("P_25014 - CORSAN Topobatimetrias") e virava
                               "P_25014…", escondendo justamente o que se quer ler.
                               Corta só o que É UUID. */
                            <span style={{ fontFamily: 'monospace' }}>
                              {/^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(String(cc.centro))
                                ? `${String(cc.centro).slice(0, 8)}…`
                                : String(cc.centro)}
                            </span>
                          ) : '—'}
                        </td>
                        <td style={{ padding: '4px 6px', textAlign: 'right', color: '#666' }}>{horas}</td>
                        <td style={{ padding: '4px 6px', textAlign: 'right', fontWeight: 600 }}>
                          {fmt(cc.valor)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </Card>
          )}

          {conta.baixas_ca && conta.baixas_ca.length > 0 && (
            <Card size="small" style={{ marginBottom: 12 }} bodyStyle={{ padding: 12 }}
              title={`💰 Pagamentos efetuados (${conta.baixas_ca.length})`}
              extra={pagoEmParte(conta) ? (
                <Text style={{ fontSize: 12 }}>
                  Pago {brl(pagoJa)} de {brl(Math.abs(Number(conta.valor) || 0))} ·{' '}
                  <Text strong style={{ color: '#E07A1F' }}>falta {brl(faltaPagar)}</Text>
                </Text>
              ) : undefined}>
              <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ color: '#888', background: '#FAFBFF' }}>
                    <th style={{ padding: '4px 6px', textAlign: 'left' }}>Data</th>
                    <th style={{ padding: '4px 6px', textAlign: 'left' }}>Conta</th>
                    <th style={{ padding: '4px 6px', textAlign: 'left' }}>Método</th>
                    <th style={{ padding: '4px 6px', textAlign: 'left' }}>Origem</th>
                    <th style={{ padding: '4px 6px', textAlign: 'right' }}>Valor</th>
                    {editavel && pagoPorPartes && <th style={{ padding: '4px 6px' }} />}
                  </tr>
                </thead>
                <tbody>
                  {conta.baixas_ca.map((b, i) => (
                    <tr key={b.id || i} style={{ borderTop: '0.5px solid #f0f0f0' }}>
                      <td style={{ padding: '4px 6px' }}
                        title={b.data_informada ? `Data digitada: ${fmtDataBr(b.data_informada)} — vale a do extrato do banco` : undefined}>
                        {b.data_pagamento ? fmtDataBr(b.data_pagamento) : '—'}
                      </td>
                      <td style={{ padding: '4px 6px' }}>{b.conta_financeira?.nome || '—'}</td>
                      <td style={{ padding: '4px 6px', color: '#666' }}>{b.metodo_pagamento || '—'}</td>
                      <td style={{ padding: '4px 6px', color: '#666' }}>
                        {origemDoPagamento(b)}{b.transacao_id && ehParteDaTela(b) ? ' · conferido no extrato' : ''}
                      </td>
                      <td style={{ padding: '4px 6px', textAlign: 'right', fontWeight: 600 }}>
                        {fmt(valorDaBaixa(b))}
                      </td>
                      {editavel && pagoPorPartes && (
                        <td style={{ padding: '2px 6px', textAlign: 'right' }}>
                          {ehParteDaTela(b) && b.id && (
                            <Popconfirm title="Desfazer este pagamento?"
                              description="O valor volta a ficar em aberto no lançamento."
                              okText="Desfazer" cancelText="Cancelar" okButtonProps={{ danger: true }}
                              onConfirm={() => desfazerParte(String(b.id))}>
                              <Button size="small" type="text" danger icon={<UndoOutlined />}
                                loading={desfazendo === b.id}>Desfazer</Button>
                            </Popconfirm>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          )}

          {conta.observacoes && (
            <Card size="small" title="📝 Observações" style={{ marginBottom: 12 }} bodyStyle={{ padding: 12 }}>
              <Text style={{ fontSize: 12, whiteSpace: 'pre-wrap' }}>{conta.observacoes}</Text>
            </Card>
          )}

          {/* Auditoria — colapsável (muito secundário, polui visualmente quando aberto) */}
          <details style={{ marginTop: 4 }}>
            <summary style={{ cursor: 'pointer', fontSize: 10, color: '#888',
              textTransform: 'uppercase', letterSpacing: 0.6, padding: '4px 0',
              userSelect: 'none' }}>
              🔍 Auditoria (técnico)
            </summary>
            <div style={{ padding: '10px 12px', background: '#FAFBFF',
              borderRadius: 4, marginTop: 6, fontSize: 11 }}>
              <DetalheRow label="ID" valor={conta.id} mono />
              <DetalheRow label="Origem (tabela)" valor={conta.origem === 'manual' ? 'lancamentos_manuais' : 'lancamentos_pagar'} mono />
              <DetalheRow label="Criado em" valor={conta.criado_em ? new Date(conta.criado_em).toLocaleString('pt-BR') : '—'} />
            </div>
          </details>
        </div>
      )}

      {/* Janela de pagamentos (07/10/2026): uma linha por conta de onde o
          dinheiro saiu; o lançamento só vira "Pago" quando as linhas cobrem o
          valor. Ex.: honorário de R$ 16.793,48 = Caixa 10.000 + Inter 6.793,48. */}
      {partesForm && (() => {
        const problema = problemaNasPartes(partesForm, faltaPagar, hojeIso);
        const resta = faltaDepois(partesForm, faltaPagar);
        const muda = (k: number, campo: Partial<ParteForm>) =>
          setPartesForm(partesForm.map((p, i) => (i === k ? { ...p, ...campo } : p)));
        return (
          <Modal open onCancel={() => setPartesForm(null)} onOk={registrarPartes}
            confirmLoading={registrando} title="💰 Registrar pagamento" centered destroyOnClose
            okText="Registrar" cancelText="Cancelar" okButtonProps={{ disabled: !!problema }}
            width="94vw" style={{ maxWidth: 860, top: 16 }}
            styles={{ body: { maxHeight: 'calc(100vh - 130px)', overflowY: 'auto', overflowX: 'hidden' } }}>
            <Form layout="vertical" size="middle" component={false}>
            <Flex gap={16} wrap style={{ marginBottom: 12, fontSize: 13 }}>
              <span>Valor do lançamento: <b>{brl(Math.abs(Number(conta.valor) || 0))}</b></span>
              {pagoJa > 0 && <span>Já pago: <b>{brl(pagoJa)}</b></span>}
              <span>Falta pagar: <b style={{ color: '#E07A1F' }}>{brl(faltaPagar)}</b></span>
            </Flex>
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
              Pagou por mais de uma conta? Uma linha para cada conta. Pagou só uma parte agora?
              Registre a parte — o lançamento fica em aberto com o que falta.
            </Text>
            {partesForm.map((p, k) => (
              <Row key={p.chave} gutter={8} align="bottom" style={{ marginBottom: 4 }}>
                <Col xs={24} md={8}>
                  <Form.Item label={k === 0 ? 'Conta' : undefined} required={k === 0} style={{ marginBottom: 8 }}>
                    <Select value={p.conta_financeira_id || undefined} placeholder="De onde saiu o dinheiro"
                      showSearch optionFilterProp="label" style={{ width: '100%' }}
                      options={opcoesConta(p.conta_financeira_id)}
                      onChange={(v) => muda(k, { conta_financeira_id: v || '' })} />
                  </Form.Item>
                </Col>
                <Col xs={12} md={5}>
                  <Form.Item label={k === 0 ? 'Valor (R$)' : undefined} required={k === 0} style={{ marginBottom: 8 }}>
                    <InputNumber {...PROPS_DINHEIRO_BR} style={{ width: '100%' }} min={0}
                      value={p.valor ?? undefined}
                      onChange={(v) => muda(k, { valor: v === null || v === undefined ? null : Number(v) })} />
                  </Form.Item>
                </Col>
                <Col xs={12} md={5}>
                  <Form.Item label={k === 0 ? 'Data' : undefined} required={k === 0} style={{ marginBottom: 8 }}>
                    <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" allowClear={false}
                      disabledDate={(d) => !!d && d.format('YYYY-MM-DD') > hojeIso}
                      value={p.data_pagamento ? dayjs(p.data_pagamento) : null}
                      onChange={(d) => muda(k, { data_pagamento: d ? d.format('YYYY-MM-DD') : '' })} />
                  </Form.Item>
                </Col>
                <Col xs={20} md={5}>
                  <Form.Item label={k === 0 ? 'Método' : undefined} style={{ marginBottom: 8 }}>
                    <Select value={p.metodo_pagamento || undefined} placeholder="—" allowClear
                      onChange={(v) => muda(k, { metodo_pagamento: v || '' })}
                      options={['PIX', 'TED', 'Boleto', 'Cartão', 'Dinheiro'].map(x => ({ label: x, value: x }))} />
                  </Form.Item>
                </Col>
                <Col xs={4} md={1} style={{ paddingBottom: 12 }}>
                  {partesForm.length > 1 && (
                    <Button type="text" danger icon={<MinusCircleOutlined />} title="Tirar esta linha"
                      onClick={() => setPartesForm(partesForm.filter((_, i) => i !== k))} />
                  )}
                </Col>
              </Row>
            ))}
            <Button type="dashed" icon={<PlusOutlined />} style={{ marginBottom: 12 }}
              disabled={partesForm.length >= 10}
              onClick={() => setPartesForm([...partesForm, novaParte(sugestaoProximaParte(partesForm, faltaPagar), partesForm.length)])}>
              Outra conta
            </Button>
            {problema ? (
              <Alert type="warning" showIcon message={problema} />
            ) : resta > 0 ? (
              <Alert type="info" showIcon
                message={`Registrando ${brl(somaPartes(partesForm))}. Fica faltando ${brl(resta)} — o lançamento continua em aberto até o resto ser pago.`} />
            ) : (
              <Alert type="success" showIcon message={`Registrando ${brl(somaPartes(partesForm))} — o lançamento fica quitado.`} />
            )}
            </Form>
          </Modal>
        );
      })()}

      {/* Sub-modal: baixar pagamento (só "Editar pagamento" de quem foi pago à moda antiga) */}
      {baixaForm && (
        <Modal open onCancel={() => setBaixaForm(null)} onOk={aplicarBaixa}
          confirmLoading={baixando} title="💰 Baixar pagamento" centered
          okText="Registrar" cancelText="Cancelar" width={460} destroyOnClose>
          <Form layout="vertical" size="small">
            <Form.Item label="Data do pagamento" required>
              <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY"
                value={baixaForm.data_pagamento ? dayjs(baixaForm.data_pagamento) : null}
                onChange={(d) => setBaixaForm({ ...baixaForm, data_pagamento: d ? d.format('YYYY-MM-DD') : '' })} />
            </Form.Item>
            <Form.Item label="Conta bancária">
              {/* FZ 5.2: seleciona do cadastro (grava a FK); texto livre só
                  como fallback se as contas não carregarem. */}
              {contasFin.length ? (
                <Select
                  value={baixaForm.conta_id || undefined}
                  onChange={(v) => {
                    const cf = contasFin.find((c: any) => c.id === v);
                    setBaixaForm({
                      ...baixaForm,
                      conta_id: cf ? cf.id : '',
                      conta: cf ? (cf.apelido || cf.nome) : '',
                    });
                  }}
                  allowClear showSearch optionFilterProp="label" placeholder="—"
                  options={opcoesConta(baixaForm.conta_id)} />
              ) : (
                <Input value={baixaForm.conta} onChange={e => setBaixaForm({ ...baixaForm, conta: e.target.value })}
                  placeholder="ex: Itaú CC 12345-6" />
              )}
            </Form.Item>
            <Form.Item label="Método de pagamento">
              <Select value={baixaForm.metodo} onChange={(v) => setBaixaForm({ ...baixaForm, metodo: v })}
                placeholder="Selecione…" allowClear
                options={[
                  { label: 'PIX', value: 'PIX' },
                  { label: 'TED', value: 'TED' },
                  { label: 'Boleto', value: 'Boleto' },
                  { label: 'Cartão', value: 'Cartão' },
                  { label: 'Dinheiro', value: 'Dinheiro' },
                ]} />
            </Form.Item>
          </Form>
        </Modal>
      )}
    </Modal>
  );
}

// Tab Histórico do modal — lista versões com diff
// Tab Anexos do modal — lista + upload + download + delete
function AnexosLista({ anexos, uploadando, onUpload, onBaixar, onDeletar, podeEditar }: {
  anexos: any[] | null;
  uploadando: boolean;
  onUpload: (file: File) => Promise<void>;
  onBaixar: (id: string) => Promise<void>;
  onDeletar: (id: string, nome: string) => Promise<void>;
  podeEditar: boolean;
}) {
  function fmtBytes(b: number) {
    if (!b) return '—';
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
    return `${(b / 1024 / 1024).toFixed(2)} MB`;
  }
  return (
    <div>
      {podeEditar && (
        <div style={{ marginBottom: 12 }}>
          <input
            type="file"
            id="anexo-upload-cp"
            style={{ display: 'none' }}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) onUpload(f).then(() => { (e.target as HTMLInputElement).value = ''; });
            }}
          />
          <Button type="primary" loading={uploadando}
            onClick={() => document.getElementById('anexo-upload-cp')?.click()}
            disabled={uploadando}>
            📎 Adicionar anexo
          </Button>
          <Text type="secondary" style={{ fontSize: 11, marginLeft: 8 }}>
            Comprovantes, NFs, contratos…
          </Text>
        </div>
      )}
      {!podeEditar && (
        <Alert type="info" style={{ marginBottom: 12, fontSize: 11 }} showIcon
          message="Lançamento somente leitura — upload de anexo bloqueado." />
      )}
      {anexos === null ? (
        <div style={{ padding: 20, textAlign: 'center', color: '#888' }}>Carregando...</div>
      ) : anexos.length === 0 ? (
        <Empty description="Nenhum anexo" style={{ padding: 20 }} />
      ) : (
        <div style={{ maxHeight: 400, overflowY: 'auto' }}>
          {anexos.map((a) => (
            <div key={a.id} style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '8px 10px', marginBottom: 6, background: '#FAFBFF', borderRadius: 4,
              borderLeft: '3px solid #0930D0',
            }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 500, color: '#020D7F',
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  📄 {a.nome}
                </div>
                <div style={{ fontSize: 10, color: '#888' }}>
                  {fmtBytes(a.tamanho_bytes || 0)} · {a.mime || '—'}
                  {a.criado_em ? ` · ${new Date(a.criado_em).toLocaleString('pt-BR')}` : ''}
                  {a.criado_por ? ` · ${a.criado_por}` : ''}
                </div>
              </div>
              <Space size={4}>
                <Button size="small" onClick={() => onBaixar(a.id)}>Baixar</Button>
                {podeEditar && (
                  <Button size="small" danger onClick={() => onDeletar(a.id, a.nome)}>
                    Excluir
                  </Button>
                )}
              </Space>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function HistoricoLista({ historico }: { historico: any[] | null }) {
  if (historico === null) {
    return <div style={{ padding: 20, textAlign: 'center', color: '#888' }}>Carregando...</div>;
  }
  if (historico.length === 0) {
    return <Empty description="Nenhuma edição registrada" style={{ padding: 20 }} />;
  }
  return (
    <div style={{ maxHeight: 480, overflowY: 'auto' }}>
      {historico.map((h, idx) => {
        const snap = typeof h.snapshot === 'string' ? JSON.parse(h.snapshot) : h.snapshot;
        return (
          <div key={h.id} style={{ marginBottom: 14, padding: 10, background: '#FAFBFF', borderRadius: 4, borderLeft: '3px solid #0930D0' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
              <Text strong style={{ fontSize: 12 }}>v{h.versao} — {h.alterado_por || '—'}</Text>
              <Text type="secondary" style={{ fontSize: 11 }}>
                {h.alterado_em ? new Date(h.alterado_em).toLocaleString('pt-BR') : ''}
              </Text>
            </div>
            {h.motivo && (
              <div style={{ fontSize: 11, color: '#666', marginBottom: 6 }}>📝 {h.motivo}</div>
            )}
            {Array.isArray(h.campos_alterados) && h.campos_alterados.length > 0 && (
              <Space size={4} wrap style={{ marginBottom: 6 }}>
                {h.campos_alterados.map((c: string) => <Tag key={c} color="blue" style={{ fontSize: 10 }}>{c}</Tag>)}
              </Space>
            )}
            <div style={{ fontSize: 10, color: '#888' }}>
              Estado <strong>anterior</strong>: valor={snap?.valor}, status={snap?.status_ca || 'em_aberto'},
              {' '}vencimento={snap?.data_vencimento ? new Date(snap.data_vencimento).toLocaleDateString('pt-BR') : '—'}
              {idx === historico.length - 1 && <Text style={{ marginLeft: 6, color: '#999' }}>(criação)</Text>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function DetalheRow({ label, valor, hint, mono }: { label: string; valor: string; hint?: string; mono?: boolean }) {
  return (
    <Flex gap={10} style={{ fontSize: 12 }}>
      <div style={{ width: 150, color: '#666', flexShrink: 0 }}>
        {label}
        {hint && <div><Text type="secondary" style={{ fontSize: 9 }}>{hint}</Text></div>}
      </div>
      <div style={{ flex: 1, color: '#222', fontFamily: mono ? 'monospace' : undefined, fontSize: mono ? 10 : 12, wordBreak: 'break-all' }}>
        {valor}
      </div>
    </Flex>
  );
}
